import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import { fmtDateTime, fmtKamas, fmtKamasLong, fmtRemaining, useServerClock } from './auctionUtils';
import './Encheres.css';

const FILTERS = [
    { id: 'all', labelKey: 'auctionAdmin.filterAll', label: 'Toutes' },
    { id: 'bids', labelKey: 'auctionAdmin.filterBids', label: 'Avec mise' },
    { id: 'none', labelKey: 'auctionAdmin.filterNone', label: 'Sans mise' },
    { id: 'unpaid', labelKey: 'auctionAdmin.filterUnpaid', label: 'Non payées' }
];

const AuctionDetailPage = () => {
    const { serverId, auctionId } = useParams();
    const { success, error: showError } = useNotification();
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [openLot, setOpenLot] = useState(null);
    const [closing, setClosing] = useState(false);
    const [editing, setEditing] = useState(null);   // zone en cours de correction { _id, zone, donjon }
    const [savingLot, setSavingLot] = useState(false);
    const now = useServerClock(data?.now);

    const load = useCallback(() => api.get(`/auctions/${serverId}/${auctionId}`)
        .then(res => { setData(res.data); setLoadError(false); })
        .catch(() => setLoadError(true)), [serverId, auctionId]);

    useEffect(() => { load(); }, [load]);

    // Rafraîchissement automatique tant que des mises sont possibles
    const running = data?.auction.running;
    useEffect(() => {
        if (!running) return undefined;
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [running, load]);

    const togglePaid = async (lot) => {
        try {
            await api.patch(`/auctions/${serverId}/lots/${lot._id}/paid`, { paid: !lot.paid });
            setData(prev => ({ ...prev, lots: prev.lots.map(l => (l._id === lot._id ? { ...l, paid: !lot.paid } : l)) }));
        } catch {
            showError(t('auctionAdmin.paidError', 'Impossible de mettre à jour le paiement'));
        }
    };

    const removeBid = async (bid) => {
        if (!window.confirm(t('auctionAdmin.confirmRemoveBid', 'Retirer la mise de {name} ({amount}) ? La meilleure mise restante redevient la mise en tête.', { name: bid.bidderName, amount: fmtKamas(bid.amount) }))) return;
        try {
            await api.delete(`/auctions/${serverId}/bids/${bid._id}`);
            success(t('auctionAdmin.bidRemoved', 'Mise retirée'));
            load();
        } catch (err) {
            showError(err?.response?.data?.error || t('auctionAdmin.removeBidError', 'Impossible de retirer la mise'));
        }
    };

    const saveLot = async (e) => {
        e.preventDefault();
        if (!editing?.zone.trim()) return;
        setSavingLot(true);
        try {
            const res = await api.patch(`/auctions/${serverId}/lots/${editing._id}`, { zone: editing.zone, donjon: editing.donjon });
            setData(prev => ({ ...prev, lots: prev.lots.map(l => (l._id === editing._id ? { ...l, ...res.data } : l)) }));
            setEditing(null);
            success(t('auctionAdmin.lotSaved', 'Zone modifiée'));
        } catch (err) {
            showError(err?.response?.data?.error || t('auctionAdmin.lotSaveError', 'Impossible de modifier la zone'));
        } finally {
            setSavingLot(false);
        }
    };

    const deleteLot = async (lot) => {
        const message = lot.bidCount > 0
            ? t('auctionAdmin.confirmDeleteLotBids', 'Supprimer la zone « {zone} » de l\'enchère ? Ses {n} mise(s) seront aussi supprimées.', { zone: lot.zone, n: lot.bidCount })
            : t('auctionAdmin.confirmDeleteLot', 'Supprimer la zone « {zone} » de l\'enchère ?', { zone: lot.zone });
        if (!window.confirm(message)) return;
        try {
            await api.delete(`/auctions/${serverId}/lots/${lot._id}`);
            success(t('auctionAdmin.lotDeleted', 'Zone supprimée de l\'enchère'));
            if (openLot === lot._id) setOpenLot(null);
            load();
        } catch (err) {
            showError(err?.response?.data?.error || t('auctionAdmin.lotDeleteError', 'Impossible de supprimer la zone'));
        }
    };

    const close = async () => {
        if (!window.confirm(t('auctionAdmin.confirmCloseDetail', 'Clôturer maintenant ? Les zones encore ouvertes se terminent immédiatement et les résultats sont annoncés.'))) return;
        setClosing(true);
        try {
            await api.post(`/auctions/${serverId}/${auctionId}/close`);
            success(t('auctionAdmin.closed', 'Enchère clôturée'));
            load();
        } catch (err) {
            showError(err?.response?.data?.error || t('auctionAdmin.closeError', 'Impossible de clôturer'));
        } finally {
            setClosing(false);
        }
    };

    if (loadError) {
        return (
            <div className="c2-scope c2-admin-page">
                <p className="auc-error">{t('auctionAdmin.notFound', 'Enchère introuvable.')}</p>
                <Link to={`/server/${serverId}/encheres`} className="c2-btn c2-btn--ghost c2-btn--sm">{t('auctionAdmin.backToAuctions', 'Retour aux enchères')}</Link>
            </div>
        );
    }
    if (!data) {
        return <div className="c2-scope c2-admin-page"><div className="c2-loading"><div className="c2-spinner" /></div></div>;
    }

    const { auction, lots, bids } = data;
    const query = search.trim().toLowerCase();
    const visibleLots = lots
        .filter(l => filter === 'all'
            || (filter === 'bids' && l.amount > 0)
            || (filter === 'none' && l.amount === 0)
            || (filter === 'unpaid' && l.amount > 0 && l.ended && !l.paid))
        .filter(l => !query || l.zone.toLowerCase().includes(query) || (l.bidderName || '').toLowerCase().includes(query));
    const bidsOf = (lotId) => bids.filter(b => b.lotId === lotId);

    // Récap par membre : zones remportées (ou en tête si l'enchère tourne encore), total et reste à payer
    const memberMap = new Map();
    for (const l of lots) {
        if (!(l.amount > 0) || !l.bidderId) continue;
        const m = memberMap.get(l.bidderId) || { bidderId: l.bidderId, name: l.bidderName || l.bidderId, zones: [], total: 0, paid: 0 };
        m.zones.push(l.zone);
        m.total += l.amount;
        if (l.paid) m.paid += l.amount;
        memberMap.set(l.bidderId, m);
    }
    const members = [...memberMap.values()].sort((a, b) => b.total - a.total);

    const copyRecap = async () => {
        const text = [
            `${auction.title} — ${t('auctionAdmin.recapTitle', 'Récap par membre')}`,
            ...members.map(m => t('auctionAdmin.recapLine', '• {name} — {n} zone(s) ({zones}) → {total}', {
                name: m.name, n: m.zones.length, zones: m.zones.join(', '), total: fmtKamasLong(m.total)
            }))
        ].join('\n');
        try {
            await navigator.clipboard.writeText(text);
            success(t('auctionAdmin.recapCopied', 'Récap copié'));
        } catch {
            showError(t('auctionAdmin.recapCopyError', 'Impossible de copier le récap'));
        }
    };

    return (
        <div className="c2-scope c2-admin-page">
            <Link to={`/server/${serverId}/encheres`} className="c2-btn c2-btn--ghost c2-btn--sm auc-back">
                <Icon name="arrowLeft" size={16} />
                {t('navigation.auctions', 'Enchères')}
            </Link>

            <PageHeader
                icon="hammer"
                title={auction.title}
                subtitle={auction.status === 'closed'
                    ? t('auctionAdmin.closedOnDate', 'Clôturée le {date}', { date: fmtDateTime(auction.closedAt) })
                    : auction.running ? t('auctionAdmin.endsIn', 'Fin dans {time}', { time: fmtRemaining(new Date(auction.lastEndsAt).getTime() - now) }) : t('auctionAdmin.allEnded', 'Toutes les zones sont terminées')}
                actions={auction.status === 'open' && (
                    <button type="button" className="c2-btn c2-btn--danger" onClick={close} disabled={closing}>
                        <Icon name="lock" size={18} />
                        {closing ? t('auctionAdmin.closing', 'Clôture...') : t('auctionAdmin.close', 'Clôturer')}
                    </button>
                )}
            />

            <div className="auc-kpis">
                <div className="c2-tile"><span>{t('auctionAdmin.zones', 'Zones')}</span><strong>{auction.stats.lotCount}</strong></div>
                <div className="c2-tile"><span>{t('auctionAdmin.zonesWithBid', 'Zones avec mise')}</span><strong>{auction.stats.soldCount}</strong></div>
                <div className="c2-tile"><span>{t('auctionAdmin.bids', 'Mises')}</span><strong>{auction.stats.bidCount}</strong></div>
                <div className="c2-tile auc-kpis__main"><span>{t('auctionAdmin.total', 'Total')}</span><strong>{fmtKamas(auction.stats.totalAmount)}</strong></div>
                <div className="c2-tile"><span>{t('auctionAdmin.paidSingular', 'Payé')}</span><strong>{auction.stats.paidCount}/{auction.stats.soldCount}</strong></div>
            </div>

            {members.length > 0 && (
                <section className="c2-card c2-section">
                    <div className="c2-section__head">
                        <h2 className="c2-section__title">
                            <Icon name="users" size={20} />{t('auctionAdmin.recapTitle', 'Récap par membre')}
                            {auction.running && <span className="c2-chip">{t('auctionAdmin.recapProvisional', 'Provisoire')}</span>}
                        </h2>
                        <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={copyRecap}>
                            <Icon name="copy" size={16} />{t('auctionAdmin.copy', 'Copier')}
                        </button>
                    </div>
                    <div className="c2-table-wrap">
                        <table className="c2-table auc-table">
                            <thead>
                                <tr>
                                    <th>{t('auctionAdmin.member', 'Membre')}</th>
                                    <th>{t('auctionAdmin.zones', 'Zones')}</th>
                                    <th className="is-num">{t('auctionAdmin.total', 'Total')}</th>
                                    <th className="is-num">{t('auctionAdmin.leftToPay', 'Reste à payer')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.map(m => (
                                    <tr key={m.bidderId}>
                                        <td><strong>{m.name}</strong></td>
                                        <td><span className="c2-muted">{m.zones.length} · </span>{m.zones.join(', ')}</td>
                                        <td className="is-num"><strong className="auc-amount">{fmtKamas(m.total)}</strong></td>
                                        <td className="is-num">
                                            {m.total - m.paid > 0
                                                ? fmtKamas(m.total - m.paid)
                                                : <span className="c2-chip c2-chip--green">{t('auctionAdmin.paidSingular', 'Payé')}</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="map" size={20} />{t('auctionAdmin.zones', 'Zones')}</h2>
                    <div className="auc-tools">
                        <div className="auc-filters" role="tablist" aria-label={t('auctionAdmin.filterZones', 'Filtrer les zones')}>
                            {FILTERS.map(f => (
                                <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className={filter === f.id ? 'is-active' : undefined} onClick={() => setFilter(f.id)}>
                                    {t(f.labelKey, f.label)}
                                </button>
                            ))}
                        </div>
                        <input type="search" className="c2-input c2-input--sm auc-search" placeholder={t('auctionAdmin.searchPlaceholder', 'Zone ou joueur...')} aria-label={t('common.search', 'Rechercher')} value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                </div>

                <div className="c2-table-wrap">
                    <table className="c2-table auc-table">
                        <thead>
                            <tr>
                                <th>{t('auctionAdmin.zone', 'Zone')}</th>
                                <th className="is-num">{t('auctionAdmin.bestBid', 'Meilleure mise')}</th>
                                <th>{t('auctionAdmin.player', 'Joueur')}</th>
                                <th className="is-center">{t('auctionAdmin.bids', 'Mises')}</th>
                                <th>{t('auctionAdmin.end', 'Fin')}</th>
                                <th className="is-center">{t('auctionAdmin.paidSingular', 'Payé')}</th>
                                <th className="is-center"><span className="visually-hidden">{t('auctionAdmin.actions', 'Actions')}</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleLots.map(l => {
                                const remaining = new Date(l.endsAt).getTime() - now;
                                const ended = l.ended || remaining <= 0;
                                const isOpen = openLot === l._id;
                                return (
                                    <React.Fragment key={l._id}>
                                        <tr className={l.amount > 0 ? 'has-bid' : undefined}>
                                            <td>
                                                {editing?._id === l._id ? (
                                                    <form className="auc-lot-edit" onSubmit={saveLot}>
                                                        <input
                                                            className="c2-input c2-input--sm"
                                                            value={editing.zone}
                                                            onChange={e => setEditing(prev => ({ ...prev, zone: e.target.value }))}
                                                            placeholder={t('auctionAdmin.zone', 'Zone')}
                                                            aria-label={t('auctionAdmin.zoneName', 'Nom de la zone')}
                                                            autoFocus
                                                        />
                                                        <input
                                                            className="c2-input c2-input--sm"
                                                            value={editing.donjon}
                                                            onChange={e => setEditing(prev => ({ ...prev, donjon: e.target.value }))}
                                                            placeholder={t('auctionAdmin.dungeonOptional', 'Donjon (optionnel)')}
                                                            aria-label={t('auctionAdmin.dungeon', 'Donjon')}
                                                        />
                                                        <div className="auc-lot-edit__actions">
                                                            <button type="submit" className="c2-btn c2-btn--accent c2-btn--sm" disabled={savingLot || !editing.zone.trim()}>
                                                                <Icon name="save" size={14} />{t('auctionAdmin.save', 'Enregistrer')}
                                                            </button>
                                                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => setEditing(null)}>
                                                                {t('auctionAdmin.cancel', 'Annuler')}
                                                            </button>
                                                        </div>
                                                    </form>
                                                ) : (
                                                    <>
                                                        <button type="button" className="auc-zone-btn" onClick={() => setOpenLot(isOpen ? null : l._id)} aria-expanded={isOpen} disabled={l.bidCount === 0}>
                                                            {l.bidCount > 0 && <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={14} />}
                                                            <strong>{l.zone}</strong>
                                                        </button>
                                                        {l.donjon && <span className="auc-sub">{l.donjon}</span>}
                                                    </>
                                                )}
                                            </td>
                                            <td className="is-num">{l.amount > 0 ? <strong className="auc-amount">{fmtKamas(l.amount)}</strong> : <span className="c2-muted">—</span>}</td>
                                            <td>{l.bidderName || <span className="c2-muted">—</span>}</td>
                                            <td className="is-center">{l.bidCount || <span className="c2-muted">0</span>}</td>
                                            <td>
                                                {ended
                                                    ? <span className="c2-muted">{l.amount > 0 ? t('auctionAdmin.won', 'Remportée') : t('auctionAdmin.unsold', 'Invendue')}</span>
                                                    : <span className={remaining < auction.antiSnipeMinutes * 60000 ? 'auc-soon' : undefined}>{fmtRemaining(remaining)}</span>}
                                            </td>
                                            <td className="is-center">
                                                {ended && l.amount > 0 ? (
                                                    <input type="checkbox" className="c2-check" checked={l.paid} onChange={() => togglePaid(l)} aria-label={t('auctionAdmin.paidFor', 'Payé : {zone}', { zone: l.zone })} />
                                                ) : <span className="c2-muted">—</span>}
                                            </td>
                                            <td className="is-center">
                                                <div className="auc-lot-actions">
                                                    <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => setEditing({ _id: l._id, zone: l.zone, donjon: l.donjon || '' })} title={t('auctionAdmin.editLot', 'Modifier la zone')} aria-label={t('auctionAdmin.editLotFor', 'Modifier : {zone}', { zone: l.zone })}>
                                                        <Icon name="pencil" size={14} />
                                                    </button>
                                                    <button type="button" className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger" onClick={() => deleteLot(l)} title={t('auctionAdmin.deleteLot', 'Supprimer la zone de l\'enchère')} aria-label={t('auctionAdmin.deleteLotFor', 'Supprimer : {zone}', { zone: l.zone })}>
                                                        <Icon name="trash" size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="auc-bids-row">
                                                <td colSpan={7}>
                                                    <ul className="auc-bids">
                                                        {bidsOf(l._id).map(b => (
                                                            <li key={b._id} className={b.removed ? 'is-removed' : undefined}>
                                                                <span className="c2-muted">{fmtDateTime(b.createdAt)}</span>
                                                                <span>{b.bidderName}</span>
                                                                <strong>{fmtKamas(b.amount)}</strong>
                                                                {b.removed ? (
                                                                    <span className="c2-chip">{t('auctionAdmin.removed', 'Retirée')}</span>
                                                                ) : (
                                                                    <button type="button" className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger" onClick={() => removeBid(b)} title={t('auctionAdmin.removeBidTitle', 'Retirer cette mise (fausse mise)')} aria-label={t('auctionAdmin.removeBid', 'Retirer cette mise')}>
                                                                        <Icon name="trash" size={14} />
                                                                    </button>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {visibleLots.length === 0 && (
                                <tr><td colSpan={7} className="is-center c2-muted">{t('auctionAdmin.noZone', 'Aucune zone')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default AuctionDetailPage;
